import React, { useEffect, useState } from "react";
import useQueryParams from "@/hooks/useQueryParams";
import SelectInput from "@/components/shared/inputs/SelectInput";
import { Label } from "@/components/ui/label";
import ColumnsDialog from "./ColumnsModal";
import ExportDialog from "@/components/shared/comman/modals/ExportModal";
import InputField from "@/components/shared/inputs/InputField";
import { X } from "lucide-react";
import StatusBadge from "../shared/badges/Badge";
import Search from "@/assets/comman/Search";
import Export from "@/assets/comman/Export";
import Plus from "@/assets/comman/Plus";
import Filter from "@/assets/comman/Filter";
import { FiltersModal } from "./FiltersModal";
import { useTranslation } from "react-i18next";

const Filters = ({
  table,
  enableSearch = true,
  enableExport = true,
  enableSort = true,
  enableColumns = true,
  enableFilter = true,
  FILTERS,
  title,
  count,
  actions,
  onSelectionChange,
  queryKey = "filter",
  modalKeys,
  view = "table",
  activeModal,
  setActiveModal,
  setRowSelection,
  searchKey,
  belowToolbar,
}) => {
  const { t } = useTranslation();
  const { getParam, setParam, setOnlyParam } = useQueryParams();
  const [search, setSearch] = useState(false);
  const activeFilter = getParam(queryKey) || FILTERS?.[0]?.value;
  const closeModal = () => setActiveModal?.("");
  const [searchValue, setSearchValue] = useState(getParam(searchKey) || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      setParam(searchKey, searchValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);

  return (
    <>
      <div className="mb-3 mt-[0.5rem] flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        {/* FILTER TABS */}
        <div className="flex flex-wrap items-center gap-2">
          {title ? (
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold capitalize">{title}</h1>
              {count !== undefined && (
                <StatusBadge status="primary" text={count} />
              )}
            </div>
          ) : (
            <div className="flex flex-col">
              <div className={`flex items-center ${belowToolbar?"gap-1":"gap-2"}  `}>
                {FILTERS?.map((item) => {
                  const isActive = activeFilter === item.value;
                  const label = item.labelKey ? t(item.labelKey) : item.label;

                  return (
                    <div
                      key={item.value}
                      role="presentation"
                      onClick={() => {
                        setOnlyParam(queryKey, item.value);
                        onSelectionChange([]);
                        setRowSelection({});
                      }}
                      className={`shrink-0 relative cursor-pointer whitespace-nowrap ${belowToolbar?"px-5 pb-2":"px-3 py-1 "}  text-sm transition ${
                        isActive
                          ? `text-primary font-semibold ${belowToolbar ? " " : "border border-[#D8EBF7] rounded-full bg-[#D8EBF7]"}`
                          : `text-muted-foreground font-medium hover:text-foreground ${belowToolbar?"":"border border-blue-300 rounded-full"}`
                      }`}
                    >
                      {label}
                      {isActive&&belowToolbar && (
                        <span className="absolute bottom-0 left-0 right-0 h-1 z-10 rounded-full bg-primary" />
                      )}
                    </div>
                  );
                })}
              </div>
              {belowToolbar&&<div className="h-px w-full bg-[#BAC8E2] relative bottom-0.5" />}
              
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="flex w-full flex-wrap items-center justify-start gap-3 text-sm text-secondary sm:w-auto sm:justify-end">
          {search ? (
            <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="relative top-4 min-w-0 flex-1">
                <InputField
                  className="mb-4 px-8 !w-full rounded-[16px] shadow-light-sm"
                  placeholder={t("common.tableToolbar.searchPlaceholder")}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />

                <button
                  type="button"
                  className="absolute right-2 top-2"
                  onClick={() => setSearch(false)}
                >
                  <X className="h-5 w-5  cursor-pointer" />
                </button>

                <Search className="absolute left-3 top-3 h-4 w-4 cursor-pointer" />
              </div>
              {actions ? (
                <div className="flex shrink-0 items-center justify-start sm:justify-end sm:pt-2">
                  {actions}
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {enableExport && modalKeys && (
                <div
                  role="presentation"
                  className="flex items-center gap-1.5 cursor-pointer font-medium"
                  onClick={() => setActiveModal?.("export")}
                >
                  <Export className="w-4 h-4" />
                  {t("common.tableToolbar.exportAs")}
                </div>
              )}

              {enableSort && (
                <div className="flex items-center gap-2">
                  <Label className="font-light">
                    {t("common.tableToolbar.sortBy")}
                  </Label>
                  <SelectInput
                    options={[
                      {
                        label: t("common.tableToolbar.ascending"),
                        value: "asc",
                      },
                      {
                        label: t("common.tableToolbar.descending"),
                        value: "desc",
                      },
                    ]}
                    value={getParam("order") || "asc"}
                    onChange={(value) => setParam("order", value)}
                    className="w-full border-none shadow-none mb-0 font-bold !px-0 text-sm"
                  />
                </div>
              )}

              {enableColumns && view === "table" && modalKeys && (
                <div
                  role="presentation"
                  className="flex items-center gap-1.5 cursor-pointer shadow rounded-full px-4 py-2"
                  onClick={() => setActiveModal?.("columns")}
                >
                  <Plus className="w-3 h-3" color="var(--color-blue-500)" />
                  {t("common.tableToolbar.columns")}
                </div>
              )}

              {enableFilter && (
                <div
                  role="presentation"
                  className="flex items-center gap-2  cursor-pointer shadow rounded-full px-4 py-2"
                  onClick={() => setActiveModal?.("filters")}
                >
                  <Filter className="w-4 h-4" />
                  {t("common.tableToolbar.filter")}
                </div>
              )}

              {enableSearch && (
                <div
                  className="shadow rounded-full p-2 cursor-pointer"
                  onClick={() => setSearch(true)}
                >
                  <Search className="h-5 w-5" />
                </div>
              )}

              {actions}
            </>
          )}
        </div>
      </div>

      {belowToolbar}

      {/* MODALS */}
      {modalKeys && (
        <>
          <ColumnsDialog
            open={activeModal === "columns"}
            onOpenChange={closeModal}
            table={table}
          />
          <FiltersModal
            open={activeModal === "filters"}
            onOpenChange={closeModal}
            table={table}
          />
          <ExportDialog
            open={activeModal === "export"}
            onOpenChange={(next) => !next && closeModal()}
          />
        </>
      )}
    </>
  );
};

export default Filters;
